import { useDisclosure } from '@heroui/react';
import { type Key, useCallback, useMemo } from 'react';
import { BiDotsHorizontal } from 'react-icons/bi';
import { FaEdit } from 'react-icons/fa';
import { FaTrash } from 'react-icons/fa6';
import { iconClasses } from '@/config/job';
import { ActionDropdown, Confirm, cn, toaster } from '@/core/ui';
import { type BlogPostFieldsFragment, BlogPostsDocument, DeleteBlogDocument } from '@/graphql/gql/graphql';
import '@/graphql/mutation/blog';
import { useMutation } from '@/libs';
import { useBlog } from '@/store/blog';

const BlogActions = {
  EDIT: {
    key: 'edit',
    className: 'text-xs py-1',
    startContent: <FaEdit className={cn(iconClasses)} />,
    title: 'Edit',
  },
  DELETE: {
    key: 'delete',
    className: 'text-xs py-1',
    startContent: <FaTrash className={cn(iconClasses, 'text-danger-300')} />,
    title: 'Delete',
    color: 'danger' as const,
  },
};

function parseBlogNumericId(id: string | null | undefined): number | null {
  if (id == null || id === '') return null;
  const n = Number.parseInt(id, 10);
  return Number.isFinite(n) ? n : null;
}

export interface BlogItemActionsProps {
  blogId: string | null | undefined;
  title: string;
  post?: BlogPostFieldsFragment | null;
}

export function BlogItemActions({ blogId, post, title }: BlogItemActionsProps) {
  const openBlogEditor = useBlog.use.openBlogEditor();
  const { isOpen, onOpen, onOpenChange } = useDisclosure();
  const [deleteBlog, { loading: deleteLoading }] = useMutation(DeleteBlogDocument, {
    refetchQueries: [BlogPostsDocument],
  });

  const numericId = useMemo(() => parseBlogNumericId(blogId), [blogId]);

  const handleDeleteConfirm = useCallback(async () => {
    if (numericId == null) return;
    await toaster.toast.promise(
      async () => {
        const res = await deleteBlog({ variables: { id: numericId } });
        if (!res.data?.admin?.deleteBlogs) {
          throw new Error('Failed to delete the blog');
        }
      },
      {
        loading: 'Deleting blog…',
        success: 'Blog deleted',
        error: (err: Error) => err.message,
      },
      { position: 'top-center' },
    );
  }, [deleteBlog, numericId]);

  const handleAction = (key: Key) => {
    switch (key) {
      case 'edit': {
        if (post) {
          openBlogEditor(post);
        }
        break;
      }
      case 'delete': {
        onOpen();
        break;
      }
      default:
        break;
    }
  };

  const actionItems = useMemo(() => [BlogActions.EDIT, BlogActions.DELETE], []);

  const disabledKeys = useMemo(() => {
    const keys: string[] = [];
    if (numericId == null) keys.push('delete');
    if (!post) keys.push('edit');
    return keys.length > 0 ? new Set(keys) : undefined;
  }, [numericId, post]);

  if (!blogId) {
    return null;
  }

  return (
    <>
      <Confirm
        type='danger'
        backdrop='opaque'
        isOpen={isOpen}
        isLoading={deleteLoading}
        title='Delete blog'
        message={
          <>
            Are you sure you want to delete <b>{title}</b>?
          </>
        }
        onOpenChange={onOpenChange}
        onConfirm={handleDeleteConfirm}
      />
      <ActionDropdown
        offset={0}
        classNames={{
          content: 'shadow-sm border border-default-200 rounded-md min-w-[130px]',
        }}
        disabledKeys={disabledKeys}
        onAction={handleAction}
        items={actionItems}
      >
        <div className='hover:bg-content2 flex justify-center items-center rounded-md px-1 py-0.5 cursor-pointer text-default-400 shrink-0'>
          <BiDotsHorizontal className='size-5 cursor-pointer text-default-400' aria-label='Blog actions' />
        </div>
      </ActionDropdown>
    </>
  );
}
